// Shared clickable stat card for the billing filter card rows, same pattern
// as the dashboard home StatsBar: click to filter, click again to clear.
// The big value is a preformatted string so pages can show a count (charges)
// or a money total (one-time discounts) without changing the card itself.

interface StatCardProps {
    label: string;
    icon: React.ReactNode;
    value: string;
    unit: string;
    isActive: boolean;
    onClick: () => void;
    /**
     * Renders the card as a non-interactive, muted placeholder — for metrics
     * whose backing data doesn't exist yet, so no invented count is ever shown.
     */
    disabled?: boolean;
    disabledReason?: string;
}

export function StatCard({
    label,
    icon,
    value,
    unit,
    isActive,
    onClick,
    disabled = false,
    disabledReason
}: StatCardProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={disabled ? disabledReason : undefined}
            className={`group flex-1 min-w-0 flex flex-col justify-center px-5 py-3.5 rounded-xl border-[1.5px] text-left transition-all duration-200 ${
                disabled
                    ? 'bg-gray-50 border-gray-200 cursor-not-allowed'
                    : `cursor-pointer hover:-translate-y-px active:translate-y-0 ${
                          isActive
                              ? 'bg-yellow-50 border-yellow-400 shadow-sm'
                              : 'bg-white border-gray-200 shadow-sm hover:shadow hover:border-gray-300'
                      }`
            }`}
        >
            <div className="flex items-center gap-2 mb-1">
                <span
                    className={`flex-shrink-0 transition-colors duration-200 ${
                        disabled
                            ? 'text-gray-300'
                            : isActive
                              ? 'text-yellow-500'
                              : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                >
                    {icon}
                </span>
                <span
                    className={`text-[11px] font-semibold tracking-[0.04em] uppercase transition-colors duration-200 ${
                        disabled
                            ? 'text-gray-300'
                            : isActive
                              ? 'text-gray-700'
                              : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                >
                    {label}
                </span>
            </div>
            <div className="flex items-baseline gap-1.5 pl-[22px] min-w-0">
                {/* The count must never clip - the descriptive unit gives way instead. */}
                <span
                    className={`text-[26px] font-bold leading-none tabular-nums flex-shrink-0 ${disabled ? 'text-gray-300' : 'text-gray-800'}`}
                >
                    {value}
                </span>
                <span
                    className={`text-xs font-medium truncate ${disabled ? 'text-gray-300' : 'text-gray-400'}`}
                >
                    {unit}
                </span>
            </div>
        </button>
    );
}
